/**
 * Holidays and special days on a time of day rule, PBX-neutral and browser-safe.
 *
 * Neutral value: a JSON list of { name, from, to, hours }, in date order.
 * - from / to: "YYYY-MM-DD" for one year, or "MM-DD" for every year (to may equal from).
 * - hours: "" means no hours that day (closed all day, for an "open hours" rule); otherwise
 *   the day's hours in the same format as a weekday ("9:00A-1:00P").
 * A value the phone system holds in a form this module can't read is kept as "raw:<text>",
 * shown but never rewritten unless someone starts the list over.
 */

export interface Holiday {
  name: string;
  from: string;
  to: string;
  hours: string;
}

const FIXED = /^(\d{4})-(\d{2})-(\d{2})$/;
const YEARLY = /^(\d{2})-(\d{2})$/;
const TIME = '(?:[01]?\\d|2[0-4])(?::[0-5]\\d)?(?:[AP]M?)?';
const RANGE = new RegExp(`^${TIME}-${TIME}$`, 'i');
export const MAX_HOLIDAYS = 60;

export interface DateParts {
  year: number | null;
  month: number;
  day: number;
}

export function dateParts(value: string): DateParts | null {
  const f = FIXED.exec(value);
  if (f) return valid({ year: Number(f[1]), month: Number(f[2]), day: Number(f[3]) });
  const y = YEARLY.exec(value);
  if (y) return valid({ year: null, month: Number(y[1]), day: Number(y[2]) });
  return null;
}

function valid(p: DateParts): DateParts | null {
  if (p.month < 1 || p.month > 12 || p.day < 1) return null;
  const days = new Date(Date.UTC(p.year ?? 2024, p.month, 0)).getUTCDate(); // 2024: Feb 29 is fine yearly
  return p.day <= days ? p : null;
}

const pad = (n: number) => String(n).padStart(2, '0');

export function dateText(p: DateParts): string {
  return p.year === null ? `${pad(p.month)}-${pad(p.day)}` : `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** Sort key: yearly dates sort by month and day, after nothing in particular. */
function sortKey(h: Holiday): string {
  const p = dateParts(h.from);
  if (!p) return `9999${h.from}`;
  return `${p.year ?? '0000'}-${pad(p.month)}-${pad(p.day)}|${h.name}`;
}

/** The list, or null when the value isn't a list this module wrote or can read. */
export function parseHolidays(value: string | undefined): Holiday[] | null {
  const v = (value ?? '').trim();
  if (v === '') return [];
  if (!v.startsWith('[')) return null;
  try {
    const raw: unknown = JSON.parse(v);
    if (!Array.isArray(raw)) return null;
    const out: Holiday[] = [];
    for (const r of raw) {
      if (!r || typeof r !== 'object') return null;
      const o = r as Record<string, unknown>;
      const from = typeof o.from === 'string' ? o.from.trim() : '';
      const to = typeof o.to === 'string' && o.to.trim() ? o.to.trim() : from;
      out.push({ name: typeof o.name === 'string' ? o.name.trim() : '', from, to, hours: typeof o.hours === 'string' ? o.hours.trim().replace(/\s+/g, ' ') : '' });
    }
    return out;
  } catch {
    return null;
  }
}

/** The canonical text for a list: same holidays, same text, whatever order they were added in. */
export function formatHolidays(list: readonly Holiday[]): string {
  if (list.length === 0) return '';
  const sorted = [...list]
    .map((h) => ({ name: h.name.trim(), from: h.from.trim(), to: (h.to || h.from).trim(), hours: h.hours.trim().replace(/\s+/g, ' ').toUpperCase() }))
    .sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
  return JSON.stringify(sorted);
}

/** Plain-language problem with a list, or null. */
export function checkHolidays(value: string): string | null {
  if (value.startsWith('raw:')) return 'These holidays were set up in the phone system in a form Stitch can’t edit. Start the list over to change it';
  const list = parseHolidays(value);
  if (list === null) return 'Not a list of holidays';
  if (list.length > MAX_HOLIDAYS) return `At most ${MAX_HOLIDAYS} holidays`;
  for (const h of list) {
    const label = h.name || h.from || 'A holiday';
    if (h.name.length > 40) return `${label}: the name can be at most 40 characters`;
    if (/["\\<>]/.test(h.name)) return `${label}: the name can't contain quotes, backslashes or angle brackets`;
    const a = dateParts(h.from);
    const b = dateParts(h.to);
    if (!a) return `${label}: "${h.from}" is not a date`;
    if (!b) return `${label}: "${h.to}" is not a date`;
    if ((a.year === null) !== (b.year === null)) return `${label}: both dates must be every year, or both in a given year`;
    if (dateText(b) < dateText(a)) return `${label}: the last day is before the first`;
    if (a.year !== null && b.year !== null && (Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86_400_000 > 31) {
      return `${label}: at most 31 days in one entry`;
    }
    const bad = h.hours.split(/\s+/).filter(Boolean).find((r) => !RANGE.test(r));
    if (bad) return `${label}: "${bad}" is not a time range like 9:00A-1:00P`;
  }
  return null;
}

// --- dates people recognise ------------------------------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Dec 25", "Dec 25, 2026", "Dec 24–26", "every Dec 25". */
export function holidayWhen(h: Holiday): string {
  const a = dateParts(h.from);
  const b = dateParts(h.to);
  if (!a) return h.from;
  const one = (p: DateParts) => `${MONTHS[p.month - 1]} ${p.day}`;
  let text = one(a);
  if (b && dateText(b) !== dateText(a)) text += `–${b.month === a.month ? b.day : one(b)}`;
  if (a.year === null) return `every ${text}`;
  return text;
}

/** The first day this holiday falls on, on or after `today` (YYYY-MM-DD), or null if it is over. */
export function nextDay(h: Holiday, today: string): string | null {
  const a = dateParts(h.from);
  const b = dateParts(h.to);
  if (!a || !b) return null;
  if (a.year !== null) {
    const end = dateText(b);
    if (end < today) return null;
    return dateText(a) < today ? today : dateText(a);
  }
  const year = Number(today.slice(0, 4));
  for (const y of [year, year + 1]) {
    const start = dateText({ ...a, year: y });
    const end = dateText({ ...b, year: y });
    if (end >= today) return start < today ? today : start;
  }
  return null;
}

/** Holidays still to come, soonest first. */
export function upcoming(list: readonly Holiday[], today: string): { holiday: Holiday; on: string }[] {
  return list
    .map((h) => ({ holiday: h, on: nextDay(h, today) }))
    .filter((x): x is { holiday: Holiday; on: string } => x.on !== null)
    .sort((a, b) => a.on.localeCompare(b.on));
}

/** Holidays whose dates are all in the past. */
export function isPast(h: Holiday, today: string): boolean {
  return nextDay(h, today) === null;
}

// --- presets -----------------------------------------------------------------------------------

/** The n-th weekday (0 = Sunday) of a month; n = -1 for the last one. */
function nthWeekday(year: number, month: number, weekday: number, n: number): number {
  if (n > 0) {
    const first = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
    return 1 + ((weekday - first + 7) % 7) + (n - 1) * 7;
  }
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const last = new Date(Date.UTC(year, month - 1, lastDay)).getUTCDay();
  return lastDay - ((last - weekday + 7) % 7);
}

/**
 * When a holiday falls on a weekend, most US businesses close on the nearest weekday instead
 * (Saturday -> Friday, Sunday -> Monday), as federal offices do.
 */
function observed(year: number, month: number, day: number): string {
  const d = new Date(Date.UTC(year, month - 1, day));
  const wd = d.getUTCDay();
  if (wd === 6) d.setUTCDate(d.getUTCDate() - 1);
  if (wd === 0) d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

const day = (year: number, month: number, d: number) => dateText({ year, month, day: d });
const one = (name: string, date: string): Holiday => ({ name, from: date, to: date, hours: '' });

export type HolidaySet = 'us-federal' | 'us-business';

export const HOLIDAY_SETS: readonly { id: HolidaySet; label: string; description: string }[] = [
  { id: 'us-business', label: 'Common US business holidays', description: 'New Year’s Day, Memorial Day, Independence Day, Labor Day, Thanksgiving and the day after, Christmas Eve and Christmas Day.' },
  { id: 'us-federal', label: 'All US federal holidays', description: 'The 11 federal holidays, on the weekday they’re observed.' }
];

/** A year's holidays for a preset set, closed all day, on the day they are observed. */
export function holidaySet(set: HolidaySet, year: number): Holiday[] {
  const thanksgiving = nthWeekday(year, 11, 4, 4);
  const common = [
    one('New Year’s Day', observed(year, 1, 1)),
    one('Memorial Day', day(year, 5, nthWeekday(year, 5, 1, -1))),
    one('Independence Day', observed(year, 7, 4)),
    one('Labor Day', day(year, 9, nthWeekday(year, 9, 1, 1))),
    one('Thanksgiving', day(year, 11, thanksgiving)),
    one('Christmas Day', observed(year, 12, 25))
  ];
  if (set === 'us-business') {
    const christmas = observed(year, 12, 25);
    const eve = new Date(`${christmas}T00:00:00Z`);
    eve.setUTCDate(eve.getUTCDate() - 1);
    const eveText = eve.getUTCDay() === 0 || eve.getUTCDay() === 6 ? null : eve.toISOString().slice(0, 10);
    return [
      ...common,
      one('Day after Thanksgiving', day(year, 11, thanksgiving + 1)),
      ...(eveText ? [one('Christmas Eve', eveText)] : [])
    ];
  }
  return [
    ...common,
    one('Martin Luther King Jr. Day', day(year, 1, nthWeekday(year, 1, 1, 3))),
    one('Washington’s Birthday', day(year, 2, nthWeekday(year, 2, 1, 3))),
    one('Juneteenth', observed(year, 6, 19)),
    one('Columbus Day', day(year, 10, nthWeekday(year, 10, 1, 2))),
    one('Veterans Day', observed(year, 11, 11))
  ];
}

/** Add holidays to a list, skipping any already there for the same first day. */
export function mergeHolidays(list: readonly Holiday[], add: readonly Holiday[]): Holiday[] {
  const have = new Set(list.map((h) => h.from));
  return [...list, ...add.filter((h) => !have.has(h.from))];
}
